import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, track } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Mail, Phone, CheckCircle2, ArrowLeft } from "lucide-react";

export default function AccountCreate() {
  const { readingId } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState(null); // null | email | phone
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", otp: "" });
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const done = () => { track("account_created", { reading_id: readingId }); navigate(`/reading/${readingId}`, { replace: true }); };
  const setF = (p) => setForm((f) => ({ ...f, ...p }));

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + `/reading/${readingId}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const emailSubmit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { email: form.email, password: form.password, name: form.name });
      setUser(data.user); done();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const sendOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/request-otp", { phone: form.phone });
      setOtpSent(true);
      if (data.dev_otp) toast.info(`Dev OTP: ${data.dev_otp}`);
    } catch (e) { toast.error("Could not send OTP"); } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/verify-otp", { phone: form.phone, otp: form.otp, name: form.name });
      setUser(data.user); done();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-md px-5 pt-10 md:pt-16">
      <div className="mb-6 flex flex-col items-center text-center">
        <CheckCircle2 className="h-12 w-12 text-green-600" />
        <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight" data-testid="account-title">Save your reading</h1>
        <p className="mt-2 text-muted-foreground">Create a free account to access your reading anytime.</p>
      </div>

      {!mode && (
        <div className="space-y-3">
          <Button data-testid="auth-google" onClick={google} className="w-full rounded-full border border-border bg-card py-6 text-foreground btn-lift hover:bg-card">
            Continue with Google
          </Button>
          <Button data-testid="auth-phone" onClick={() => setMode("phone")} variant="outline" className="w-full rounded-full py-6 btn-lift">
            <Phone className="mr-2 h-4 w-4" /> Continue with phone
          </Button>
          <Button data-testid="auth-email" onClick={() => setMode("email")} variant="outline" className="w-full rounded-full py-6 btn-lift">
            <Mail className="mr-2 h-4 w-4" /> Continue with email
          </Button>
          <button data-testid="maybe-later" onClick={() => navigate(`/reading/${readingId}`, { replace: true })} className="mt-3 block w-full text-center text-sm text-muted-foreground underline hover:text-foreground">
            Maybe later
          </button>
          <p className="pt-2 text-center text-xs text-muted-foreground">Your reading stays accessible in this session even if you skip.</p>
        </div>
      )}

      {mode === "email" && (
        <div className="space-y-3">
          <button onClick={() => setMode(null)} className="flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="h-4 w-4" /> Back</button>
          <Input data-testid="email-name" placeholder="Your name" value={form.name} onChange={(e) => setF({ name: e.target.value })} className="rounded-xl bg-background" />
          <Input data-testid="email-input" type="email" placeholder="Email" value={form.email} onChange={(e) => setF({ email: e.target.value })} className="rounded-xl bg-background" />
          <Input data-testid="email-password" type="password" placeholder="Password (min 6 chars)" value={form.password} onChange={(e) => setF({ password: e.target.value })} className="rounded-xl bg-background" />
          <Button data-testid="email-submit" onClick={emailSubmit} disabled={busy || form.password.length < 6 || !form.email} className="w-full rounded-full bg-foreground py-6 text-background hover:bg-foreground">Create account</Button>
        </div>
      )}

      {mode === "phone" && (
        <div className="space-y-3">
          <button onClick={() => { setMode(null); setOtpSent(false); }} className="flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="h-4 w-4" /> Back</button>
          <Input data-testid="phone-input" placeholder="Phone number" value={form.phone} onChange={(e) => setF({ phone: e.target.value })} className="rounded-xl bg-background" />
          {!otpSent ? (
            <Button data-testid="send-otp" onClick={sendOtp} disabled={busy || form.phone.length < 8} className="w-full rounded-full bg-foreground py-6 text-background hover:bg-foreground">Send OTP</Button>
          ) : (
            <>
              <Input data-testid="otp-input" placeholder="Enter 6-digit OTP" value={form.otp} onChange={(e) => setF({ otp: e.target.value })} className="rounded-xl bg-background" />
              <Button data-testid="verify-otp" onClick={verifyOtp} disabled={busy || form.otp.length < 6} className="w-full rounded-full bg-foreground py-6 text-background hover:bg-foreground">Verify & continue</Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
